import React, { CSSProperties } from 'react';
import { ChildrenProp, ClassNameProp } from '../common-types';

/**
 * Properties for a {@link GridList} component.
 * @author agd
 */
export interface GridListProps extends Required<ChildrenProp>, ClassNameProp {
  /**
   * The number of columns in the grid.
   */
  cols: number;
  /**
   * Element spacing. This results in a padding on the child elements.
   */
  spacing?: number;
  /**
   * Inline CSS to add to the element.
   */
  style?: CSSProperties;
  /**
   * Height of each grid row.
   */
  cellHeight?: number | 'auto';
}

/**
 * GridList component for layouting purposes. This component has been taken and adapted from the
 * react material-ui component library (see {@link https://material-ui.com/components/grid-list/}).
 * This component is not responsive, so if you want responsive behavior use it in combination with `react-laag/useBreakpoint`.
 * Unless you have a specific reason to use this component, it is recommended to use {@link Grid} instead
 *
 * @param properties a {@link GridListProps} object
 * @return the created {@link JSX.Element}.
 * @author agd
 * @constructor
 */
export function GridList({
  cols,
  spacing = 4,
  cellHeight = 'auto',
  className = '',
  ...props
}: GridListProps): JSX.Element {
  return (
    <div className={`ehs-grid-list ${className}`.trim()}>
      {React.Children.map(props.children, child => {
        if (!React.isValidElement(child)) {
          return null;
        }

        const childCols = child.props.cols || 1;
        const childRows = child.props.rows || 1;

        return React.cloneElement(child, {
          style: {
            width: `${(100 / cols) * childCols}%`,
            height:
              cellHeight === 'auto' ? 'auto' : cellHeight * childRows + spacing,
            padding: spacing / 2,
            boxSizing: 'border-box',
            ...child.props.style
          }
        });
      })}
    </div>
  );
}
